/**
 * Sort state management hook for the DataList component.
 *
 * Manages the sort toggle cycle (none -> asc -> desc -> none), exposes
 * `setSort` for programmatic control, and optionally sorts data client-side
 * when `config.mode` is not `'server'`.
 *
 * @see Spec section 7
 * @see Plan section 3.2
 */

import { useState, useMemo, useCallback, useEffect } from 'react';

import type { SortingConfig, SortState } from '../types';
import { safeString } from '../utils/cellRenderer';

// ---------------------------------------------------------------------------
// In-memory sort store – survives component remounts within the same SPA
// session but resets on full page reload (e.g. login redirect).
// ---------------------------------------------------------------------------

const sortStore = new Map<string, SortState[]>();

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

/**
 * Shape returned by `useListSorting`.
 */
export interface UseListSortingReturn {
  /** Current sort state (array of column + direction pairs). */
  sortState: SortState[];

  /** Toggle sort direction on a column following the cycle: none -> asc -> desc -> none. */
  toggleSort: (column: string) => void;

  /** Set the sort state directly. */
  setSort: (sort: SortState[]) => void;

  /** Data sorted client-side when applicable; otherwise the original data array. */
  sortedData: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Type-aware value comparator.
 *
 * - `null` / `undefined` values always sort to the end regardless of direction.
 * - Numbers are compared numerically.
 * - Strings are compared with `localeCompare`.
 * - Mixed or unknown types are coerced to strings for comparison.
 */
function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  // Nulls/undefined always sort to end
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  let result: number;

  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = safeString(a).localeCompare(safeString(b));
  }

  return direction === 'desc' ? -result : result;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages sort state and optionally performs client-side sorting.
 *
 * @param config       - Sorting configuration (may be `undefined` if sorting
 *                       is not configured for this list).
 * @param data         - The raw data array to sort (or pass through).
 * @param getCellValue - Accessor function that extracts a cell value from a
 *                       row given a column key. Used by the client-side
 *                       comparator.
 * @param listId       - Stable list identifier. When `config.persistSort` is
 *                       set, the sort state is cached under this id so it
 *                       survives remounts within the SPA session.
 *
 * @returns Sort state, toggle/set actions, and the (potentially sorted) data.
 */
export function useListSorting(
  config: SortingConfig | undefined,
  data: Record<string, unknown>[],
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
  listId?: string,
): UseListSortingReturn {
  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------

  const persistSort = config?.persistSort === true;
  const storeKey = persistSort && listId ? listId : null;

  const [sortState, setSortState] = useState<SortState[]>(() => {
    if (storeKey) return sortStore.get(storeKey) ?? config?.defaultSort ?? [];
    return config?.defaultSort ?? [];
  });

  useEffect(() => {
    if (storeKey) sortStore.set(storeKey, sortState);
  }, [sortState, storeKey]);

  // -----------------------------------------------------------------------
  // toggleSort
  // -----------------------------------------------------------------------

  /**
   * Cycles a column through the sort directions defined by `SORT_CYCLE`:
   *
   *   none -> asc -> desc -> none
   *
   * Single-column sort only -- toggling a new column replaces any existing
   * sort rather than adding a secondary sort level.
   */
  const toggleSort = useCallback((column: string) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.column === column);

      if (!existing) {
        // Currently none -> asc  (SORT_CYCLE index 0 -> 1)
        return [{ column, direction: 'asc' as const }];
      }

      if (existing.direction === 'asc') {
        // Currently asc -> desc  (SORT_CYCLE index 1 -> 2)
        return [{ column, direction: 'desc' as const }];
      }

      // Currently desc -> none  (SORT_CYCLE index 2 -> wrap to 0)
      return [];
    });
  }, []);

  // -----------------------------------------------------------------------
  // setSort
  // -----------------------------------------------------------------------

  const setSort = useCallback((sort: SortState[]) => {
    setSortState(sort);
  }, []);

  // -----------------------------------------------------------------------
  // sortedData (memoised)
  // -----------------------------------------------------------------------

  /**
   * When in server mode sorting is handled by the API, so we return data
   * as-is. In client mode (or when no mode is specified and data is inline)
   * we sort a shallow copy of the data array.
   */
  const sortedData = useMemo<Record<string, unknown>[]>(() => {
    // No data -- nothing to sort.
    if (data.length === 0) return data;

    // Server mode -- the API handles sorting.
    if (config?.mode === 'server') return data;

    // No active sort -- return data unchanged.
    if (sortState.length === 0) return data;

    // Client-side sort: shallow-copy to avoid mutating the source array.
    const sorted = [...data];

    sorted.sort((rowA, rowB) => {
      for (const { column, direction } of sortState) {
        const a = getCellValue(rowA, column);
        const b = getCellValue(rowB, column);
        const cmp = compareValues(a, b, direction);
        if (cmp !== 0) return cmp;
      }
      return 0;
    });

    return sorted;
  }, [data, config?.mode, sortState, getCellValue]);

  // -----------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------

  return {
    sortState,
    toggleSort,
    setSort,
    sortedData,
  };
}
